import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { FALLBACK_PLANS } from '../services/api';
import type { InternetPlan } from '../types';
import { clearLeadContext } from '../utils/leadContext';

type SelectedPlanState = {
  selectedPlan?: number;
  selectedPlanName?: string;
  selectedPlanPrice?: number;
  selectedPlanSpeed?: string;
};

/** Only allow /check-service when coming from the home/plans signup flow. */
function hasSignupParams(search: string): boolean {
  const qs = new URLSearchParams(search);
  const lat = parseFloat(qs.get('lat') || '');
  const lng = parseFloat(qs.get('lng') || '');
  const address = (qs.get('address') || '').trim();
  const skip = qs.get('skipToContact') === '1' || qs.get('skipToContact') === 'true';
  const verified = qs.get('verified') === '1' || qs.get('verified') === 'true';
  return !!(address && Number.isFinite(lat) && Number.isFinite(lng) && (skip || verified));
}

function resolvePlanFromNav(
  search: string,
  state: SelectedPlanState | null
): Partial<InternetPlan> | null {
  const qs = new URLSearchParams(search);
  const packageName = (qs.get('package') || qs.get('plan') || state?.selectedPlanName || '').trim();
  const planIdRaw = qs.get('planId') || (state?.selectedPlan != null ? String(state.selectedPlan) : '');
  const planId = planIdRaw ? Number(planIdRaw) : NaN;

  const byId = Number.isFinite(planId)
    ? FALLBACK_PLANS.find((p) => p.id === planId)
    : undefined;
  const byName = packageName
    ? FALLBACK_PLANS.find((p) => p.name.toLowerCase() === packageName.toLowerCase())
    : undefined;

  const matched = byId || byName;
  if (matched) return matched;

  if (packageName || state?.selectedPlanPrice != null) {
    return {
      id: Number.isFinite(planId) ? planId : undefined,
      name: packageName || undefined,
      price: state?.selectedPlanPrice ?? (qs.get('price') ? Number(qs.get('price')) : undefined),
      speed: qs.get('speed') || state?.selectedPlanSpeed || undefined,
    };
  }
  return null;
}

function buildIframeSrc(search: string, plan: Partial<InternetPlan> | null): string {
  const params = new URLSearchParams(search);
  params.set('embed', '1');

  if (plan?.name && !params.get('package')) params.set('package', plan.name);
  if (plan?.price != null && !params.get('price')) params.set('price', String(plan.price));
  if (plan?.speed && !params.get('speed')) params.set('speed', plan.speed);
  if (plan?.id != null && !params.get('planId')) params.set('planId', String(plan.id));

  return `https://airmaxcloud.com/plugin-api/public/check-service.php?${params.toString()}`;
}

const CheckService: React.FC = () => {
  const location = useLocation();
  const navState = (location.state as SelectedPlanState | null) || null;
  const allowed = hasSignupParams(location.search);

  const selectedPlan = useMemo(
    () => (allowed ? resolvePlanFromNav(location.search, navState) : null),
    [allowed, location.search, navState]
  );

  const lockedSrcRef = useRef<string | null>(null);
  if (allowed && !lockedSrcRef.current) {
    lockedSrcRef.current = buildIframeSrc(location.search, selectedPlan);
  }
  if (allowed) {
    const nextSrc = buildIframeSrc(location.search, selectedPlan);
    if (
      selectedPlan?.name &&
      lockedSrcRef.current &&
      !lockedSrcRef.current.includes(`package=${encodeURIComponent(selectedPlan.name)}`) &&
      location.search.includes('package=')
    ) {
      lockedSrcRef.current = nextSrc;
    }
  }
  const iframeSrc = lockedSrcRef.current || '';

  const [height, setHeight] = useState(window.innerWidth <= 768 ? 1100 : 900);

  useEffect(() => {
    if (!allowed) return;
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [allowed, iframeSrc]);

  useEffect(() => {
    if (!allowed) return;
    const style = document.createElement('style');
    style.id = 'hide-airmax-chat';
    style.textContent = `
      [id*="airmax-chat"], [class*="airmax-chat"],
      [id*="airmax-widget"], [class*="airmax-widget"] {
        display: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.getElementById('hide-airmax-chat')?.remove();
      const w = window as any;
      if (w.AirmaxChat) {
        if (w.AirmaxChat.isOpen) {
          w.AirmaxChat.isOpen = false;
          const win = document.getElementById('airmax-chat-window');
          if (win) win.style.display = 'none';
        }
        w.AirmaxChat.greetingBubbleShown = false;
        w.AirmaxChat.hideGreetingBubble?.();
      }
    };
  }, [allowed]);

  useEffect(() => {
    if (!allowed) return;
    const onMessage = (e: MessageEvent) => {
      if (!e.data?.type) return;
      if (e.data.type === 'airmax-check-service-resize' || e.data.type === 'airmax-check-service-step') {
        const next = Number(e.data.height);
        if (next > 0) setHeight(Math.ceil(next + 16));
      }
      if (e.data.type === 'airmax-check-service-done') {
        clearLeadContext();
        // Address bar → home so refresh can't reopen the checker
        try {
          window.history.replaceState({}, '', '/');
        } catch (_) {}
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [allowed]);

  useEffect(() => {
    if (!allowed || !iframeSrc) return;
    const iframe = document.getElementById('airmax-check-service-iframe') as HTMLIFrameElement | null;
    const loader = document.getElementById('iframe-loader');
    if (!iframe || !loader) return;

    const handleLoad = () => {
      setTimeout(() => {
        loader.style.display = 'none';
      }, 800);
    };

    iframe.addEventListener('load', handleLoad);
    const t = setTimeout(() => {
      if (loader.style.display !== 'none') loader.style.display = 'none';
    }, 5000);

    return () => {
      iframe.removeEventListener('load', handleLoad);
      clearTimeout(t);
    };
  }, [allowed, iframeSrc]);

  // Bare /check-service (bookmark, refresh after done, Google, old links) → home
  if (!allowed) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="bg-white" style={{ paddingTop: '80px' }}>
      <div id="iframe-loader" className="text-center text-gray-500 py-6 text-sm">
        Loading availability checker…
      </div>
      <iframe
        id="airmax-check-service-iframe"
        key={iframeSrc}
        src={iframeSrc}
        title="Airmax Service Availability Checker"
        scrolling="no"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation"
        style={{
          display: 'block',
          width: '100%',
          height,
          border: 'none',
          overflow: 'hidden',
        }}
      />
    </div>
  );
};

export default CheckService;
